import React, { useEffect, useRef, useState } from "react";
import { IMyProperties } from "../../../interface/myProperty";
import { REIButton, REIDivider, REIProgressSpinner, REIToastContainer } from "../../../components/ui";
import { RadioButton } from "primereact/radiobutton";
import PortfolioTrackerHeader from "../header";
import { MEMBER_KEY, Messages, MyTrackerTabs, USER_DETAIL } from "../../../utils/constants";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { HouseIcon, BedIcon, BathIcon, CarIcon } from "../../../public/assets/images";
import { httpDelete, useHttpGets } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { IActual4, IMyPortfolio } from "../../../interface/myPortfolio";
import { IPropertyDetail } from "../../../interface/equity";
import ViewPropertyDetails from "../../../interface/Watchlist/propertyDetails";
import {
  formatCurrency,
  formatNumber,
  formatNumberWithDecimal,
  getCurrentFinancialYear,
} from "../../../utils/commonUtil";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import MapWithMarker from "../../../components/shared/mapWithMarker";
import CashFlow from "../MyTracker/CashFlow";
import { ICashflow } from "../../../interface/propertyTracker";
import { ITrackerEquity } from "../../../interface/equity";
import EquityChart from "../MyEquity/EquityGraph";
import CashFlowDetails from "./CashFlowDetails";
import EquityDetail from "../MyEquity/EquityDetails";
import InputSlider from "../../../components/ui/slider";
import EditorPortfolioTracker, { IMemberPropertyComment } from "./Notes";
import ContactDetails from "./ContactDetails";
import BasicFileUpload from "../../../components/shared/BasicFileUpload";
import AdvancedFileUpload from "../../../components/shared/AdvancedFileUpload";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";

enum PropertyDataType {
  Forecast = "forecast",
  Actuals = "actuals",
}
interface IMarker {
  lat: number;
  lng: number;
  title: string;
}

const ManageProperty: React.FC<{ propertyData: IMyProperties; handleRefresh: () => void }> = ({
  propertyData,
  handleRefresh,
}) => {
  const addressParts = [propertyData.StreetAddress, propertyData.Suburb, propertyData.State, propertyData.Postcode]
    .filter((part) => part && part.trim() !== "")
    .join(", ");

  const addressPartsWithCountry =
    addressParts && propertyData.Country ? `${addressParts} (${propertyData.Country})` : addressParts;
  const { isShowingTotals, selectedYear, setSelectedYear } = useFilterSelection();
  const initialShowTotalsRef = useRef<boolean>(isShowingTotals);
  const [selectedDataFilter, setSelectedDataFilter] = useState<PropertyDataType>(PropertyDataType.Actuals);
  const memberId = localStorage.getItem(MEMBER_KEY);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [portfolioData, setPortfolioData] = useState<IMyPortfolio>({} as IMyPortfolio);
  const [viewPropertyData, SetViewPropertyData] = useState<ViewPropertyDetails>({} as ViewPropertyDetails);
  const [isLoading, setIsLoading] = useState(true);
  const [isShowingTotal, setIsShowingTotal] = useState<boolean>(initialShowTotalsRef.current);
  const [selectedEquityOption, setSelectedEquityOption] = useState<"totals" | "portions">(
    isShowingTotal ? "totals" : "portions"
  );
  const toastRef = useRef<Toast>(null);
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [marker, setMarker] = useState<IMarker[]>([]);
  const zoom = 15;
  const [isImageComponent, setIsImageComponent] = useState<boolean>(true);
  const [cashFlowData, setCashFlowData] = useState<{
    Actuals: ICashflow[];
    Forecast: ICashflow[];
  }>();
  const [equityData, setEquityData] = useState<{
    Actuals: IActual4[];
    Forecast: IActual4[];
  }>();
  const [equityDetail, setEquityDetail] = useState<ITrackerEquity>({
    PropertyDetails: [] as IPropertyDetail[],
    TotalForDetails: {},
    TotalForGraph: {},
  } as ITrackerEquity);
  const [currentYear, setCurrentYear] = useState<number>(Number(getCurrentFinancialYear()));
  const [memberPropertyComment, setMemberPropertyComment] = useState<IMemberPropertyComment>();
  const propertyDescription = {
    ...propertyData,
    PropertyDetails: {
      ...propertyData,
      WatchlistID: propertyData.MemberPropertyId.toString(),
      isFromPropertyAnalyser: false,
      isfromPortfolioTracker: true,
    },
  };
  const [refreshData, setRefreshData] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!propertyData) {
      return;
    }

    SetViewPropertyData((prevData) => ({
      ...prevData,
      PropertyDetails: {
        ...prevData.PropertyDetails,
        WatchlistID: propertyData.MemberPropertyId.toString(),
        isFromPropertyAnalyser: true,
      },
    }));

    useHttpGets(
      `${ApiEndPoints.tracker}PortfolioTracker/MyPortfolio?MemberId=${memberId}&userCountryCode=${memberDetail.Country}&PortfolioTrackerId=${propertyData.PortfolioTrackerId}`,
      undefined
    )
      .then((result) => {
        if (result?.data) {
          const cashflowTotals = result.data?.CashflowTotals;
          const equityTotals = result.data?.EquityTotals;
          const detailLabel = result.data?.PortfolioTrackerDetailLabel;
          const firstActualYear = result.data?.CashflowInDetail?.CashflowSummary?.Actuals?.[0]?.Year;

          setPortfolioData((prevData) => {
            return result?.data;
          });
          setCurrentYear(Number(firstActualYear?.slice(2, 4) || getCurrentFinancialYear()));
          setCashFlowData({
            Actuals: (cashflowTotals?.Actuals as unknown as ICashflow[]) || [],
            Forecast: (cashflowTotals?.Forecast as unknown as ICashflow[]) || [],
          });
          setEquityData({
            Actuals: (equityTotals?.Actuals as unknown as IActual4[]) || [],
            Forecast: (equityTotals?.Forecast as unknown as IActual4[]) || [],
          });
          const propertyDetail: IPropertyDetail = {
            EquityPropertyDetail: {
              Country: detailLabel?.Country,
              State: detailLabel?.State,
              Suburb: detailLabel?.Suburb,
              StreetAddress: detailLabel?.StreetAddress,
              Postcode: detailLabel?.Postcode,
              Entity: detailLabel?.Entity,
              DateAdded: detailLabel?.DateAdded,
            },
            EquityGraphTotals: (result.data?.EquityGraphTotals as unknown as IActual4) || ({} as IActual4),
            EquityGraphPortions: (result.data?.EquityGraphPortions as unknown as IActual4) || ({} as IActual4),
            EquityInDetailsTotals: (result.data?.EquityDetails as unknown as IActual4) || ({} as IActual4),
            EquityInDetailsPortions: (result.data?.EquityDetailsPortions as unknown as IActual4) || ({} as IActual4),
          };

          setEquityDetail((prevEquityDetail) => ({
            ...prevEquityDetail,
            isPropertyClickable: false,
            PropertyDetails: [propertyDetail],
          }));
          setMemberPropertyComment(result.data?.MemberPropertyComment);
          const googleMapsUrl = result?.data?.MemberPropertyMisc?.MapURL?.split("?m=")[1];
          if (googleMapsUrl) {
            getCoordinatesFromAddress(googleMapsUrl).then((response) => {
              if (response) {
                setCenter(response);
                setMarker([{ lat: response.lat, lng: response.lng, title: googleMapsUrl }]);
              }
            });
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching portfolio data:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [propertyData, refreshData]);

  const getCoordinatesFromAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    setIsLoading(true);
    const apiKey = process.env.GOOGLE_MAPS_API_KEY;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${apiKey}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch data from Google Maps API");
      }

      const data = await response.json();

      if (data.status === "OK" && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setIsLoading(false);
        return { lat, lng };
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const removeProperty = async () => {
    setIsLoading(true);
    await httpDelete(
      `${ApiEndPoints.tracker}PortfolioTracker/Tracker?MemberId=${memberId}&PortfolioTrackerId=${propertyData.PortfolioTrackerId}`
    )
      .then((data) => {
        if (data.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.DELETE_SUCCESS,
            detail: "The Property has been removed from your portfolio",
            life: 2000,
          });
          handleRefresh();
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: Messages.DELETE_ERROR,
            detail: data.error,
            life: 2000,
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        toastRef.current?.show({
          severity: "error",
          summary: Messages.DELETE_ERROR,
          detail: error,
          life: 2000,
        });
      });
  };

  const handleDeletePropertyMethod = () => {
    confirmDialog({
      message: (
        <div className="text-lg">
          Are you sure you want to remove this property from your portfolio?
          <br />
          Removing this property will remove all record of its future forecast and all its details.
        </div>
      ),
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        removeProperty();
      },
      reject: () => {},
    });
  };

  const onPortionChange = (istotal: boolean) => {
    setIsShowingTotal(istotal);
  };

  const handleReanalyseOrEditProperty = () => {
    navigate(`/portfolio-tracker`, {
      state: {
        tab: "MyPropertyTab",
        MyPropertyId: undefined,
        AddNewProperty: false,
        EditProperty: true,
        portfolioTrackerId: propertyData.PortfolioTrackerId,
      },
    });
  };

  const renderCustomHeader = () => {
    return (
      <div className="flex flex-row justify-between bg-slate-600 text-white  rounded-t-md p-4">
        <label className="font-semibold text-lg">Summary</label>
        <REIButton
          id="mpmng-btn-reanalyse"
          label="Reanalyse or edit this property"
          classNames="text-white text-sm rounded-lg p-1 border-none px-2"
          onClick={handleReanalyseOrEditProperty}
        ></REIButton>
      </div>
    );
  };

  const returnSummaryObject = () => {
    if (isShowingTotal) {
      return selectedDataFilter == PropertyDataType.Actuals
        ? portfolioData.PortfolioTrackerDetailLabel.PropertyKeyValuesTotal
        : portfolioData.PortfolioTrackerDetailLabel.PropertyKeyValuesForecastTotal;
    } else {
      return selectedDataFilter == PropertyDataType.Actuals
        ? portfolioData.PortfolioTrackerDetailLabel.PropertyKeyValuesPortion
        : portfolioData.PortfolioTrackerDetailLabel.PropertyKeyValuesForecastPortion;
    }
  };

  const currencyTemplate = (value: string) => {
    return (
      <span className={Number(value) < 0 ? "text-red-600" : ""}>
        {Number(value) < 0 ? `(${formatCurrency(-Number(value))})` : formatCurrency(Number(value))}
      </span>
    );
  };

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value == null || value === undefined || value === 0
        ? "0.00%"
        : value < 0
          ? `(${formatNumberWithDecimal(-value, 2, 2)}%)`
          : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const renderKeyValues = () => {
    return (
      <div className="flex flex-col gap-2 md:w-2/3">
        <div className="flex md:flex-row flex-col gap-2 justify-between">
          <label className="font-semibold xl:text-lg">
            {`Entity: ${portfolioData.PortfolioTrackerDetailLabel.Entity}`.toLocaleUpperCase()}
          </label>
          <div className="flex align-bottom mb-2">
            <RadioButton
              inputId="forecast"
              name="actuals"
              data-testid="mpmng-rdo-actuals"
              value={PropertyDataType.Actuals}
              checked={selectedDataFilter == PropertyDataType.Actuals ? true : false}
              onChange={(e) => {
                setSelectedDataFilter(e.value);
              }}
            />
            <label htmlFor={PropertyDataType.Actuals} className="mx-2 ">
              Actuals
            </label>
            <RadioButton
              inputId="forecast"
              name="forecast"
              data-testid="mpmng-rdo-forecast"
              value={PropertyDataType.Forecast}
              checked={selectedDataFilter == PropertyDataType.Forecast ? true : false}
              onChange={(e) => {
                setSelectedDataFilter(e.value);
              }}
            />
            <label htmlFor={PropertyDataType.Forecast} className="mx-2 ">
              Forecast
            </label>
          </div>
          <div className="text-md">Added:{portfolioData.PortfolioTrackerDetailLabel.DateAdded}</div>
        </div>
        <div className="w-full flex md:flex-row flex-col gap-2">
          <div className="md:w-1/3 flex flex-row ">
            <img src={HouseIcon} alt="Image 1" className="" id="image" />
            <label htmlFor="image" className=" text-center align-top text-lg ml-2">
              {portfolioData.PortfolioTrackerDetailLabel.PropertyType}
            </label>
          </div>
          <div className="md:w-1/3  flex flex-row content-center gap-2">
            <img src={BedIcon} title="Bedrooms" alt="Image 2" className="" id="bedhIcon" />
            <label htmlFor="bedhIcon" className=" text-center align-top text-lg mr-2">
              {portfolioData.PortfolioTrackerDetailLabel.Bed}
            </label>
            <img src={BathIcon} title="Bathrooms" alt="Image 1" className="" id="bathIcon" />
            <label htmlFor="bathIcon" className=" text-center align-top text-lg mr-2">
              {portfolioData.PortfolioTrackerDetailLabel.Bath}
            </label>

            <img src={CarIcon} title="Parkings" alt="Image 3" className="" id="carIcon" />
            <label htmlFor="carIcon" className=" text-center align-top text-lg mr-2">
              {portfolioData.PortfolioTrackerDetailLabel.Parking}
            </label>
          </div>
          <div className="md:w-1/3 items-center md:text-end text-start lg:text-lg">
            {`Land size: ${portfolioData.PortfolioTrackerDetailLabel.Landsize.split(" ")[0]} m²`}
          </div>
        </div>
        <REIDivider></REIDivider>
        <div className="">
          <div className="w-full flex md:flex-row flex-col gap-2 mb-3">
            <div className="w-1/2 flex flex-col text-start ">
              <b className="font-bold text-2xl">
                {currencyTemplate(
                  returnSummaryObject().CurrentMarketValue === null ||
                    returnSummaryObject().CurrentMarketValue === "" ||
                    Number(returnSummaryObject().CurrentMarketValue) == 0
                    ? "0.00"
                    : returnSummaryObject().CurrentMarketValue
                )}
              </b>
              <span className="uppercase text-sm">Current market estimate*</span>
            </div>
            <div className="w-1/2 flex flex-col text-start">
              <b className="font-bold text-2xl">
                {currencyTemplate(
                  returnSummaryObject().CapitalGrowth === null ||
                    returnSummaryObject().CapitalGrowth === "" ||
                    Number(returnSummaryObject().CapitalGrowth) == 0
                    ? "0.00"
                    : returnSummaryObject().CapitalGrowth
                )}
              </b>
              <span className="uppercase text-sm">Capital growth</span>
            </div>
            <div className="w-1/2 flex flex-col text-start ">
              <b className="font-bold text-2xl">
                {currencyTemplate(
                  returnSummaryObject().NetEquity === null ||
                    returnSummaryObject().NetEquity === "" ||
                    Number(returnSummaryObject().NetEquity) == 0
                    ? "0.00"
                    : returnSummaryObject().NetEquity
                )}
              </b>
              <span className="uppercase text-sm">Net equity</span>
            </div>
          </div>

          <div className="w-full flex md:flex-row flex-col gap-2 mb-3">
            <div className="w-1/2 flex flex-col text-start">
              <b className="font-bold text-2xl">
                {returnSummaryObject().CapitalGrowthPercentage == null ||
                returnSummaryObject().CapitalGrowthPercentage === "" ||
                Number(returnSummaryObject().CapitalGrowthPercentage) == 0
                  ? "0.00%"
                  : percentageTemplate(Number(returnSummaryObject().CapitalGrowthPercentage))}
              </b>
              <span className="uppercase text-sm">Total Capital growth</span>
            </div>
            <div className="w-1/2 flex flex-col text-start ">
              <b className="font-bold text-2xl">
                {currencyTemplate(
                  returnSummaryObject().PreTaxCashflow === null ||
                    returnSummaryObject().PreTaxCashflow === "" ||
                    Number(returnSummaryObject().PreTaxCashflow) == 0
                    ? "0.00"
                    : returnSummaryObject().PreTaxCashflow
                )}
              </b>
              <span className="uppercase text-sm">Pre-tax cash flow YTD</span>
            </div>
            <div className="w-1/2 flex flex-col text-start">
              <b className="font-bold text-2xl">
                {returnSummaryObject().LVR == null ||
                returnSummaryObject().LVR === "" ||
                Number(returnSummaryObject().LVR) == 0
                  ? "0.00%"
                  : percentageTemplate(Number(returnSummaryObject().LVR))}
              </b>
              <span className="uppercase text-sm">LVR</span>
            </div>
          </div>
          <div className="w-full flex md:flex-row flex-row gap-2 mb-3">
            <div className="w-1/2 flex flex-col text-start ">
              <b className="font-bold text-2xl">
                {returnSummaryObject().DSR == null ||
                returnSummaryObject().DSR === "" ||
                Number(returnSummaryObject().DSR) == 0
                  ? "0.00"
                  : Number(returnSummaryObject().DSR)}
              </b>
              <span className="uppercase text-sm">DSR</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderImageGallery = () => {
    return (
      <div className="w-full relative lg:w-[30%] overflow-hidden  justify-center text-center">
        {isImageComponent && (
          <div className=" ">
            <BasicFileUpload propertyData={viewPropertyData.PropertyDetails.WatchlistID}></BasicFileUpload>
          </div>
        )}
        {!isImageComponent && center && (
          <div className="w-full object-fill">
            <MapWithMarker center={center} markers={marker} zoom={zoom} />
          </div>
        )}
        <div className="absolute mt-[-40px] mb-[0px] right-0 flex space-x-1 " id="mapbutton">
          <REIButton
            id="mpmng-btn-showimages"
            tooltip="Show images"
            icon="pi pi-image"
            label=""
            onClick={() => setIsImageComponent(true)}
          ></REIButton>
          <REIButton
            id="mpmng-btn-showmap"
            tooltip="Show map"
            icon="pi pi-map"
            classNames="ml-1"
            label=""
            onClick={() => setIsImageComponent(false)}
          ></REIButton>
        </div>
      </div>
    );
  };

  const renderContentComponent = () => {
    return (
      <>
        <div className="flex md:flex-row flex-col gap-2 justify-between xl:mx-4">
          {renderImageGallery()}
          {renderKeyValues()}
        </div>
        <div className="text-start">
          * Last update: {portfolioData.PortfolioTrackerDetailLabel.EstimatedMarketValueLastUpdated}
        </div>
      </>
    );
  };

  const HeaderTemplate = (
    <div className="rounded-t-lg p-2 px-4 bg-slate-600 text-white border flex gap-3 justify-around">
      <div className="flex w-1/3 content-start">
        <h3>Equity in detail</h3>
      </div>
      <div className="flex flex-row gap-3 align-middle w-1/3 content-center">
        <h3>Showing:</h3>
        <InputSlider
          id="mpmng-sldr-year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.value)}
          min={currentYear}
          max={currentYear + (currentYear < Number(getCurrentFinancialYear()) ? 10 : 9)}
          step={1}
          classNames="self-center w-1/2"
        />
        <h3>
          FY-{selectedYear}
          <span className="text-sm">{selectedYear.toString() == getCurrentFinancialYear() ? "(Actuals YTD)" : ""}</span>
        </h3>
      </div>
      <div className="w-1/3 content-center text-end gap-2">
        <REIButton
          id="mpmng-btn-updateforecast"
          label="Update forecast"
          classNames="mr-2"
          icon="pi pi-pencil"
          onClick={() => {
            navigate(`/portfolio-tracker`, {
              state: {
                tab: "MyPropertyTab",
                MyPropertyId: undefined,
                AddNewProperty: false,
                EditProperty: true,
                portfolioTrackerId: propertyData.PortfolioTrackerId,
              },
            });
          }}
        ></REIButton>
      </div>
    </div>
  );

  const handleRefreshData = () => {
    setRefreshData(!refreshData);
  };

  return (
    <>
      <REIToastContainer toastReference={toastRef}></REIToastContainer>
      <ConfirmDialog />
      {isLoading && <REIProgressSpinner />}
      {propertyData && portfolioData && !isLoading && (
        <>
          <PortfolioTrackerHeader
            tabIndex={MyTrackerTabs.ManageProperty}
            tabName="My Properties"
            children={
              <>
                <div className="flex flex-row gap-2 w-full justify-between p-4">
                  <div className="flex flex-col w-full ">
                    <div className="flex text-blue-600 -mt-9 text-left">
                      <h2>{addressPartsWithCountry}</h2>
                    </div>
                  </div>
                  <div className="flex flex-col w-full content-center gap-2 align-top">
                    {propertyData.PropertyKeyValuesUserCurrencyTotal == null &&
                      formatNumber(propertyData.PositionOwned) !== "100" && (
                        <div className="flex flex-row mx-2 justify-end">
                          <label>Showing : </label>
                          {""}
                          <RadioButton
                            inputId="totals"
                            name="tracker_dashboard_showing"
                            data-testid="mpmng-rdo-totals"
                            value="totals"
                            checked={selectedEquityOption ? selectedEquityOption === "totals" : true}
                            onChange={(e) => {
                              setSelectedEquityOption(e.value);
                              onPortionChange(true);
                            }}
                            className="ml-2"
                          />
                          <label htmlFor="tracker_dashboard_showing" className="mx-2">
                            Totals
                          </label>
                          <RadioButton
                            inputId="portions"
                            name="tracker_dashboard_showing"
                            data-testid="mpmng-rdo-portions"
                            value="portions"
                            checked={selectedEquityOption === "portions"}
                            onChange={(e) => {
                              setSelectedEquityOption(e.value);
                              onPortionChange(false);
                            }}
                          />
                          <label htmlFor="tracker_dashboard_showing" className="ml-2">
                            Entity's portion {formatNumber(propertyData.PositionOwned)}%
                          </label>
                        </div>
                      )}
                    <REIButton
                      id="mpmng-btn-remove"
                      classNames=" no-wrap self-end"
                      onClick={() => {
                        handleDeletePropertyMethod();
                      }}
                      label="Remove this property from my portfolio"
                    ></REIButton>
                  </div>
                </div>
              </>
            }
          ></PortfolioTrackerHeader>
          <REIToggablePanel
            contentComponent={renderContentComponent()}
            defaultCollapse={true}
            header=""
            customHeader={renderCustomHeader()}
            istoggleable={false}
            customClassName="mb-2"
          ></REIToggablePanel>
          <REIToggablePanel
            header="Annual Growth Performance"
            istoggleable={false}
            contentComponent={
              <div className="flex flex-col gap-2 text-start p-2">
                <span>
                  Since <b>{portfolioData.PortfolioTrackerDetailLabel.MonthYearPurchase}</b> (the month you purchased)
                  <b> {percentageTemplate(portfolioData.PortfolioTrackerDetailLabel.Growth)}</b> <br />
                </span>
                <span>
                  <b>Average growth for property type: {portfolioData.PortfolioTrackerDetailLabel.PropertyType}</b>{" "}
                  <br />
                </span>
                <span>
                  Suburb: {portfolioData.PortfolioTrackerDetailLabel.Suburb} long term average
                  <b>
                    {" "}
                    {percentageTemplate(
                      portfolioData.PortfolioTrackerDetailLabel.PropertyGrowthStatSet.StatsSuburbLongTermGrowth * 100
                    )}
                  </b>{" "}
                  Postcode: {portfolioData.PortfolioTrackerDetailLabel.PropertyGrowthStatSet.StatsPostcode} long term
                  average{" "}
                  <b>
                    {percentageTemplate(
                      portfolioData.PortfolioTrackerDetailLabel.PropertyGrowthStatSet.StatsPostcodeLongTermGrowth * 100
                    )}
                  </b>{" "}
                  Region: {portfolioData.PortfolioTrackerDetailLabel.PropertyGrowthStatSet.StatsRegion}
                  long term average{" "}
                  <b>
                    {percentageTemplate(
                      portfolioData.PortfolioTrackerDetailLabel.PropertyGrowthStatSet.StatsRegionLongTermGrowth * 100
                    )}
                  </b>
                </span>
              </div>
            }
            defaultCollapse={false}
            customClassName="mb-2"
          ></REIToggablePanel>

          <div className="flex flex-row gap-3 content-center h-full mb-2">
            <div className="w-1/2">
              <CashFlow
                trackerData={cashFlowData}
                entityId=""
                showingTotalSummary={isShowingTotal}
                year={Number("20" + selectedYear.toString())}
                showPostExpenseData={false}
                onYearChangeCallback={(year) => setSelectedYear(year)}
              />
            </div>
            <div className="w-1/2">
              <REIToggablePanel
                header={"Equity"}
                istoggleable={false}
                contentComponent={
                  <EquityChart
                    trackerData={equityData}
                    year={Number("20" + selectedYear.toString())}
                    onYearChangeCallback={(year) => setSelectedYear(year)}
                  />
                }
                defaultCollapse={false}
                customClassName="bg-gray-50 rounded-t-md h-full"
              ></REIToggablePanel>
            </div>
          </div>

          <CashFlowDetails
            portFolioData={portfolioData}
            handleRefresh={handleRefreshData}
            year={selectedYear}
            onYearChange={(e) => setSelectedYear(e.value)}
            isShowingTotal={isShowingTotal}
          ></CashFlowDetails>
          <div className="mb-2">
            <REIToggablePanel
              contentComponent={
                <EquityDetail
                  isForecastOnly={false}
                  isFooterVisible={false}
                  equityData={equityDetail}
                  year={"20" + selectedYear.toString()}
                  isFromMyPropertyShowingTotals={isShowingTotal}
                  isMyPropertyParent={true}
                />
              }
              defaultCollapse={false}
              header=""
              customClassName="mt-2"
              istoggleable={false}
              customHeader={HeaderTemplate}
            ></REIToggablePanel>
          </div>
          <ContactDetails
            propertyTrackerId={portfolioData.PortfolioTrackerDetailLabel.PortfolioTrackerId}
          ></ContactDetails>
          <div className="flex flex-row gap-3 content-center">
            <div className="w-1/2 flex flex-col h-full">
              <REIToggablePanel
                header={"Document"}
                istoggleable={false}
                contentComponent={
                  <AdvancedFileUpload
                    propertyData={propertyDescription.PropertyDetails.WatchlistID}
                  ></AdvancedFileUpload>
                }
                defaultCollapse={false}
              ></REIToggablePanel>
            </div>
            <div className="w-1/2 flex flex-col h-full">
              <REIToggablePanel
                header={"Notes"}
                istoggleable={false}
                contentComponent={
                  <EditorPortfolioTracker
                    RefreshData={handleRefreshData}
                    MemberPropertyComment={memberPropertyComment}
                    watchListId={propertyData.MemberPropertyId.toString()}
                  ></EditorPortfolioTracker>
                }
                customClassName="bg-gray-50 rounded-t-md"
                defaultCollapse={false}
              ></REIToggablePanel>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ManageProperty;
